/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2021 Priska Wermelinger & Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import addArticles from '../utils/addArticles';
import ArticleNoteHeader from './ArticleNoteHeader';
import NoteInput from './NoteInput';
import type Article from 'flarum/orell-articles/models/Article';
import type Note from '../../common/models/Note';
import type Mithril from 'mithril';

declare const s9e: {
	TextFormatter: {
		preview(text: string, target: Element): void;
	};
};

export interface ArticleNoteAttrs extends ComponentAttrs {
	article: Article;
}

export default class ArticleNote<CustomAttrs extends ArticleNoteAttrs = ArticleNoteAttrs> extends Component<CustomAttrs> {
	protected editing!: Stream<boolean>;
	protected value!: Stream<string>;
	protected textHeight: number | undefined;
	protected loading!: boolean;

	oninit(vnode: Mithril.Vnode<CustomAttrs, this>): void {
		super.oninit(vnode);

		this.resetChanges();

		this.textHeight = 0;

		this.loading = false;
	}

	onupdate(vnode: Mithril.VnodeDOM<CustomAttrs, this>): void {
		super.onupdate(vnode);

		if (!this.editing()) {
			this.resetChanges();
		}
	}

	resetChanges(): void {
		const { article } = this.attrs;
		const note = article.note();

		this.editing = Stream(false);
		this.value = Stream(note ? note.text() : '');
	}

	view(): Mithril.Children {
		const { article } = this.attrs;

		const note = article.note();
		const text = note ? note.text() : null;

		if (!text) return <div></div>;

		if (this.editing()) {
			return (
				<NoteInput
					value={this.value()}
					onChange={this.value}
					onSave={this.saveChanges.bind(this)}
					onCancel={this.resetChanges.bind(this)}
					textHeight={this.textHeight}
					loading={this.loading}
				/>
			);
		}

		const canEdit = article.bookshelf();

		return (
			<div className="ArticleNote">
				<ArticleNoteHeader />

				<div className="ArticleNote-text" oncreate={this.textOnupdate.bind(this)} onupdate={this.textOnupdate.bind(this)}>
					{text}
				</div>

				{canEdit && (
					<Button className="Button Button--flat" onclick={this.editNote.bind(this)}>
						{app.translator.trans('glowingblue-orell-bookshelf.forum.article_note.edit')}
					</Button>
				)}
			</div>
		);
	}

	textOnupdate(vnode: Mithril.VnodeDOM): void {
		const { article } = this.attrs;
		const note = article.note();
		const text = note ? note.text() : null;

		s9e.TextFormatter.preview(text || '', vnode.dom);

		this.textHeight = $(vnode.dom).innerHeight();
	}

	editNote(): void {
		this.editing(true);
	}

	saveChanges(): void {
		const { article } = this.attrs;

		const bookshelf = article.bookshelf();
		const status = article.status();

		// This view only renders once the article has a note.
		const note = article.note() as Note;

		if (note.text() === this.value()) {
			this.editing(false);
			return;
		} else {
			note.pushAttributes({ text: this.value() });
		}

		this.loading = true;

		addArticles(bookshelf || null, [article], status || null, note).then(() => {
			app.store.find<Article>(article.data.type!, article.id()!).then(() => {
				this.loading = false;
				this.editing(false);
				m.redraw();
			});
		});
	}
}
